import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  btnPrimaryClass, btnSecondaryClass, btnDangerClass,
} from "../dashboardStyles";
import {
  HEADER_ASPECT, exportBrandingImage, loadBrandingImage, validateImageFile,
} from "../../../utils/brandingImage";
import InfoTip from "./wizard/components/InfoTip";

/** Local editing only: the parent commits the resulting File on form save. */
const BrandingImageField = ({
  id, kind, label, hint, info, file, imageUrl, removed = false, disabled = false,
  onChange, onBusyChange,
}) => {
  const { t } = useTranslation();
  const key = "Dashboard.BrandingImages";
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const dragRef = useRef(null);
  const generation = useRef(0);
  const [filePreview, setFilePreview] = useState("");
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [crop, setCrop] = useState(kind === "header");
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    if (!file) {
      setFilePreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => {
    onBusyChange?.(busy || !!source);
    return () => onBusyChange?.(false);
  }, [busy, source, onBusyChange]);
  useEffect(() => {
    if (!source) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
      URL.revokeObjectURL(source.url);
    };
  }, [source]);

  const closeEditor = () => {
    generation.current += 1;
    setSource(null);
    setBusy(false);
  };

  const chooseFile = async (event) => {
    const selected = event.target.files?.[0];
    // Reset immediately: choosing the same file again must fire change.
    event.target.value = "";
    if (!selected) return;
    const token = ++generation.current;
    setError("");
    let url;
    try {
      validateImageFile(selected);
      setBusy(true);
      url = URL.createObjectURL(selected);
      const image = await loadBrandingImage(url);
      if (token !== generation.current) {
        URL.revokeObjectURL(url);
        return;
      }
      setCrop(kind === "header");
      setZoom(1);
      setPosition({ x: 0.5, y: 0.5 });
      setSource({ url, image });
    } catch (err) {
      if (url) URL.revokeObjectURL(url);
      if (token === generation.current) {
        setError(t(`${key}.errors.${err.message}`, {
          defaultValue: t(`${key}.errors.decode`),
        }));
      }
    } finally {
      if (token === generation.current) setBusy(false);
    }
  };

  const applyImage = async () => {
    const token = generation.current;
    setBusy(true);
    setError("");
    try {
      const result = await exportBrandingImage(source.image, { kind, crop, zoom, ...position });
      if (token !== generation.current) return;
      onChange(result, false);
      closeEditor();
    } catch {
      if (token === generation.current) setError(t(`${key}.errors.encode`));
    } finally {
      if (token === generation.current) setBusy(false);
    }
  };

  const preview = filePreview || (removed ? "" : imageUrl);
  const aspect = kind === "logo" ? 1 : HEADER_ASPECT;
  const imageRatio = source ? source.image.naturalWidth / source.image.naturalHeight : aspect;
  const widthFactor = Math.max(1, imageRatio / aspect) * zoom;
  const heightFactor = Math.max(1, aspect / imageRatio) * zoom;
  const cropStyle = {
    width: `${widthFactor * 100}%`,
    height: `${heightFactor * 100}%`,
    left: `${-(widthFactor - 1) * position.x * 100}%`,
    top: `${-(heightFactor - 1) * position.y * 100}%`,
  };

  const resetCrop = () => {
    setZoom(1);
    setPosition({ x: 0.5, y: 0.5 });
  };

  const startDrag = (event) => {
    if (!crop || busy) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      clientX: event.clientX, clientY: event.clientY,
      ...position, width: rect.width, height: rect.height,
    };
  };

  const moveCrop = (event) => {
    if (!dragRef.current || !crop) return;
    const { clientX, clientY, x, y, width, height } = dragRef.current;
    setPosition({
      x: widthFactor > 1 ? Math.min(1, Math.max(0, x - (event.clientX - clientX) / (width * (widthFactor - 1)))) : x,
      y: heightFactor > 1 ? Math.min(1, Math.max(0, y - (event.clientY - clientY) / (height * (heightFactor - 1)))) : y,
    });
  };

  // A small thumbnail and the actions beside it: the image is checked at a
  // glance, and the real size of it is what the preview of the page is for.
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-semibold text-ink-800">{label}</label>
        {info && <InfoTip label={t("Dashboard.Setup.moreAbout", { label })} text={info} />}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className={kind === "logo"
          ? "flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-ink-200 bg-white"
          : "flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-ink-200 bg-linear-to-br from-brand-100 to-ink-100"}>
          {preview
            ? <img src={preview} alt={label} className={kind === "logo" ? "max-h-full max-w-full object-contain p-1.5" : "size-full object-cover"} />
            : <i className={`bi ${kind === "logo" ? "bi-image" : "bi-card-image"} text-xl text-ink-400`} aria-hidden="true" />}
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          <button type="button" className={btnSecondaryClass} disabled={disabled || busy} onClick={() => inputRef.current.click()} aria-controls={id}>
            <i className={`bi ${preview ? "bi-arrow-repeat" : "bi-upload"}`} aria-hidden="true" />
            {t(`${key}.${preview ? "replace" : "choose"}`)}
          </button>
          {preview && (
            <button type="button" className={btnDangerClass} disabled={disabled || busy}
              aria-label={t(`${key}.remove`, { label })}
              onClick={() => { setError(""); onChange(null, true); }}>
              <i className="bi bi-trash3" aria-hidden="true" />
              {t(`${key}.removeShort`)}
            </button>
          )}
          {removed && (
            <button type="button" className={btnSecondaryClass} disabled={disabled || busy}
              onClick={() => onChange(null, false)}>
              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
              {t(`${key}.undo`)}
            </button>
          )}
        </div>
      </div>
      <input ref={inputRef} type="file" id={id} accept="image/jpeg,image/png,image/webp,image/gif" className="sr-only" disabled={disabled || busy} onChange={chooseFile} />
      <p className="mt-2 mb-0 text-xs leading-relaxed text-ink-500">
        {hint || t(`${key}.${kind === "logo" ? "emptyLogo" : "emptyHeader"}`)}
      </p>
      <p className="mt-1 mb-0 text-xs leading-relaxed text-ink-400">{t(`${key}.${kind === "logo" ? "logoHelp" : "headerHelp"}`)}</p>
      {file && <p className="mt-2 mb-0 text-xs font-semibold text-brand-700" role="status">{t(`${key}.ready`, { size: Math.max(1, Math.round(file.size / 1024)) })}</p>}
      {removed && <p className="mt-2 mb-0 text-xs text-ink-500" role="status">{t(`${key}.removalPending`)}</p>}
      {busy && !source && <p role="status" className="mt-2 mb-0 text-sm text-ink-500">{t(`${key}.processing`)}</p>}
      {error && !source && <p role="alert" className="mt-2 mb-0 text-sm text-red-700">{error}</p>}
      {source && (
        <dialog ref={dialogRef} aria-labelledby={`${id}-editor-title`} onCancel={(event) => { event.preventDefault(); closeEditor(); }} className="fixed inset-0 m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xl flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white p-0 text-ink-900 shadow-xl backdrop:bg-black/60 open:flex">
          <div className="flex shrink-0 items-center justify-between border-b border-ink-200 px-5 py-4">
            <h3 id={`${id}-editor-title`} className="m-0 text-lg font-bold">{t(`${key}.editorTitle`, { label })}</h3>
            <button type="button" className={btnSecondaryClass} onClick={closeEditor} aria-label={t(`${key}.cancel`)}><i className="bi bi-x-lg" aria-hidden="true" /></button>
          </div>
          <div className="min-h-0 overflow-y-auto p-5">
            <p className="mt-0 text-sm text-ink-500">{t(`${key}.${kind === "logo" ? "logoEditorHelp" : "headerEditorHelp"}`)}</p>
            {kind === "logo" && (
              <label className="mb-4 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={crop} disabled={busy}
                  onChange={(event) => { setCrop(event.target.checked); resetCrop(); }} />
                {t(`${key}.cropLogo`)}
              </label>
            )}
            <div
              className={`relative overflow-hidden rounded-xl border border-ink-200 bg-ink-100 ${crop ? "cursor-grab touch-none" : "flex items-center justify-center p-4"}`}
              style={{ aspectRatio: crop ? `${aspect}` : "16 / 9" }}
              onPointerDown={startDrag}
              onPointerMove={moveCrop}
              onPointerUp={() => { dragRef.current = null; }} onPointerCancel={() => { dragRef.current = null; }}
            >
              <img src={source.url} alt={t(`${key}.cropPreview`)} draggable="false"
                className={crop ? "pointer-events-none absolute max-w-none select-none" : "absolute inset-0 h-full w-full object-contain p-4"}
                style={crop ? cropStyle : undefined} />
              {crop && (
                <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3" aria-hidden="true">
                  {Array.from({ length: 9 }, (_, index) => <span key={index} className="border border-white/30" />)}
                </div>
              )}
              {kind === "header" && (
                <div className="pointer-events-none absolute inset-y-0 border-x-2 border-dashed border-white/90"
                  style={{ left: "5.5556%", right: "5.5556%" }} aria-hidden="true" />
              )}
            </div>
            {kind === "header" && <p className="mb-0 mt-2 text-xs text-ink-500">{t(`${key}.mobileSafeArea`)}</p>}
            {crop && <div className="mt-4 grid gap-3">
              {[
                ["zoom", zoom, 1, 3, 0.01, (value) => setZoom(value)],
                ["horizontal", position.x, 0, 1, 0.01, (value) => setPosition((current) => ({ ...current, x: value }))],
                ["vertical", position.y, 0, 1, 0.01, (value) => setPosition((current) => ({ ...current, y: value }))],
              ].map(([name, value, min, max, step, change]) => (
                <label key={name} className="flex items-center gap-3 text-sm">
                  <span className="w-28 shrink-0">{t(`${key}.${name}`)}</span>
                  <input type="range" min={min} max={max} step={step} value={value} disabled={busy}
                    onChange={(event) => change(Number(event.target.value))}
                    className="min-w-0 flex-1 accent-brand-600" />
                </label>
              ))}
              <button type="button" className={btnSecondaryClass} disabled={busy} onClick={resetCrop}>{t(`${key}.reset`)}</button>
            </div>}
            {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
            <p className="mb-0 mt-4 text-xs text-ink-500">{t(`${key}.autoOptimize`)}</p>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-ink-200 px-5 py-4">
            <button type="button" className={btnSecondaryClass} onClick={closeEditor}>{t(`${key}.cancel`)}</button>
            <button type="button" className={btnPrimaryClass} disabled={busy} onClick={applyImage}>{t(`${key}.${busy ? "processing" : "apply"}`)}</button>
          </div>
        </dialog>
      )}
    </div>
  );
};

BrandingImageField.propTypes = {
  id: PropTypes.string.isRequired, kind: PropTypes.oneOf(["logo", "header"]).isRequired,
  label: PropTypes.string.isRequired, hint: PropTypes.string, file: PropTypes.object,
  /** Opens beside the label as an (i) tip. */
  info: PropTypes.string,
  imageUrl: PropTypes.string, removed: PropTypes.bool, disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired, onBusyChange: PropTypes.func,
};

export default BrandingImageField;
